import axios from "../lib/axios";
import { AxiosError } from "axios";

/**
 * Scheme Bucket Interface
 */
export interface SchemeBucket {
  _id?: string;
  schemeBucketId: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Error Response Interface
 */
interface ErrorResponse {
  message: string;
  statusCode?: number;
}

/**
 * Create Scheme Bucket
 */
export const createSchemeBucket = async (data: { name: string }) => {
  try {
    const response = await axios.post<SchemeBucket>("/schemebucket", data);
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(
        axiosError.response.data.message ||
        "Failed to create scheme bucket"
      );
    }
    throw new Error("Network error. Please try again.");
  }
};

/**
 * Get All Scheme Buckets (supports pagination & search for future use)
 */
export const getSchemeBuckets = async (params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  sortOrder?: "asc" | "desc";
  sortBy? : string;
}) => {
  try {
    const response = await axios.get<{
      data: SchemeBucket[];
      pagination: {
        total: number;
        page: number;
        pageSize: number;
      };
    }>("/schemebucket", { params });

    return {
      data: response.data.data || [],
      pagination: response.data.pagination,
    };

  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(
        axiosError.response.data.message ||
        "Failed to fetch scheme buckets"
      );
    }
    throw new Error("Network error. Please try again.");
  }
};


/**
 * Get Scheme Bucket by ID (schemeBucketId)
 */
export const getSchemeBucketById = async (schemeBucketId: string) => {
  try {
    const response = await axios.get<SchemeBucket>(
      `/schemebucket/${schemeBucketId}`
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(
        axiosError.response.data.message ||
        "Failed to fetch scheme bucket"
      );
    }
    throw new Error("Network error. Please try again.");
  }
};

/**
 * Update Scheme Bucket (name only)
 */
export const updateSchemeBucket = async (
  schemeBucketId: string,
  updateData: { name: string }
) => {
  try {
    const response = await axios.put<SchemeBucket>(
      `/schemebucket/${schemeBucketId}`,
      updateData
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(
        axiosError.response.data.message ||
        "Failed to update scheme bucket"
      );
    }
    throw new Error("Network error. Please try again.");
  }
};

/**
 * Delete Scheme Bucket
 */
export const deleteSchemeBucket = async (schemeBucketId: string) => {
  try {
    const response = await axios.delete<{ message: string }>(
      `/schemebucket/${schemeBucketId}`
    );
    return response.data.message;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(
        axiosError.response.data.message ||
        "Failed to delete scheme bucket"
      );
    }
    throw new Error("Network error. Please try again.");
  }
};
